const REDACTED = "[REDACTED]";

// Key fragments that mark a value as sensitive (matched case-insensitively, as substrings so
// `accessToken`, `refresh_token`, `Authorization`, `set-cookie`, `client_secret` all match).
// Compliance line: Algerian data law + the backend Fernet-encrypts email payloads, so none of
// this may reach the console or an external sink.
const SENSITIVE_FRAGMENTS = [
  "password",
  "token",
  "access",
  "refresh",
  "authorization",
  "cookie",
  "secret",
];

function isSensitiveKey(key: string): boolean {
  const k = key.toLowerCase();
  if (SENSITIVE_FRAGMENTS.some((fragment) => k.includes(fragment))) {
    return true;
  }
  // Email message/body fields (encrypted payloads on the backend) — but NOT the plain
  // `email` address key, which is needed for debugging and is returned in profile payloads.
  if (
    k.includes("email") &&
    (k.includes("body") || k.includes("message") || k.includes("content"))
  ) {
    return true;
  }
  return false;
}

/**
 * Deep-redact a value for logging/forwarding: replaces sensitive keys with "[REDACTED]",
 * recursing through nested objects and arrays. Returns a NEW structure (never mutates input).
 * Runs before ANYTHING is logged or sent to the error sink.
 */
export function redact(
  value: unknown,
  seen: WeakSet<object> = new WeakSet()
): unknown {
  if (value === null || typeof value !== "object") {
    return value;
  }
  if (seen.has(value)) {
    return "[Circular]";
  }
  seen.add(value);

  if (Array.isArray(value)) {
    return value.map((item) => redact(item, seen));
  }

  const out: Record<string, unknown> = {};
  for (const [key, val] of Object.entries(value as Record<string, unknown>)) {
    out[key] = isSensitiveKey(key) ? REDACTED : redact(val, seen);
  }
  return out;
}
